import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/constants/brand";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const OPERATOR_LINKS = [
  ["stories", "STORIES", "/stories"],
  ["lemonading", "LEMONADING", "/lemonading"],
  ["learn", "LEARN", "/learn"],
  ["academy", "SMASH ACADEMY", "/academy"],
  ["story", "OUR STORY", "/our-story"],
];

const CUSTOMER_LINKS = [
  ["menu", "MENU", "/events#menu"],
  ["gallery", "GALLERY", "/events#gallery"],
  ["faq", "FAQ", "/events#faq"],
];

const CUSTOMER_PATHS = ["/events", "/catering", "/deposit"];

/** Nav adapts to the audience: catering customers never see operator pricing paths. */
const useAudience = () => {
  const { pathname } = useLocation();
  const customer = CUSTOMER_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  return customer
    ? { customer, links: CUSTOMER_LINKS, cta: ["BOOK AN EVENT", "/events#book"], home: "/events" }
    : { customer, links: OPERATOR_LINKS, cta: ["APPLY", "/apply"], home: "/operators" };
};

export const SiteNav = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { links, cta, home } = useAudience();
  const active = (to) => pathname === to || pathname.startsWith(`${to}/`);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-[#0A0A0A]/93 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3 md:px-10">
        <Link to={home} data-testid="nav-home" className="shrink-0">
          <img src={BRAND.logo} alt="Smash Lemonade" className="h-9 w-auto" />
        </Link>

        <nav className="ml-auto hidden items-center gap-6 lg:flex">
          {links.map(([key, label, to]) => (
            <Link
              key={key}
              to={to}
              data-testid={`nav-${key}`}
              className={`mono text-[11px] tracking-widest transition-colors ${
                active(to.split("#")[0]) ? "text-primary" : "text-muted-foreground hover:text-primary"
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3 lg:ml-0">
          <Link
            to="/"
            data-testid="nav-switch"
            className="mono hidden text-[11px] tracking-widest text-muted-foreground hover:text-primary md:block"
          >
            ALL OF SMASH
          </Link>
          <Link
            to={user ? "/academy-dashboard" : "/login"}
            data-testid="nav-login"
            className="mono hidden text-[11px] tracking-widest text-muted-foreground hover:text-primary sm:block"
          >
            {user ? "MY ACADEMY" : "LOG IN"}
          </Link>
          <Link
            to={cta[1]}
            data-testid="nav-cta"
            className="display rounded-sm bg-primary px-4 py-2 text-lg uppercase leading-none text-primary-foreground transition-colors hover:bg-[#E6C800] md:px-5 md:text-xl"
          >
            {cta[0]}
          </Link>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="text-muted-foreground hover:text-primary lg:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open ? (
        <nav data-testid="nav-mobile" className="border-t border-border bg-[#0A0A0A] lg:hidden">
          {[
            ...links,
            ["switch", "ALL OF SMASH", "/"],
            ["login", user ? "MY ACADEMY" : "LOG IN", user ? "/academy-dashboard" : "/login"],
          ].map(([key, label, to]) => (
            <Link
              key={key}
              to={to}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-${key}`}
              className="mono block border-b border-border/60 px-5 py-4 text-[12px] tracking-widest text-muted-foreground active:text-primary"
            >
              {label}
            </Link>
          ))}
        </nav>
      ) : null}
    </header>
  );
};

export const SiteFooter = ({ contactEmail }) => {
  const { links, cta, customer } = useAudience();
  return (
    <footer className="border-t border-border bg-[#0A0A0A] px-5 py-12 md:px-10">
      <div className="mx-auto max-w-6xl">
        {customer ? (
          <p className="display text-2xl uppercase leading-tight md:text-3xl">
            Fresh-smashed lemonade,
            <br />
            <span className="text-primary">brought to your event.</span>
          </p>
        ) : (
          <p className="display text-2xl uppercase leading-tight md:text-3xl">
            Smash Lemonade sells lemonade.
            <br />
            <span className="text-primary">Smash Academy builds operators.</span>
            <br />
            Lemonading builds a community.
          </p>
        )}
        <div className="mt-8 flex flex-wrap gap-5">
          {[...links, ["cta", cta[0], cta[1]], ["switch", "ALL OF SMASH", "/"]].map(
            ([key, label, to]) => (
              <Link
                key={key}
                to={to}
                data-testid={`footer-${key}`}
                className="mono text-[10px] tracking-widest text-muted-foreground hover:text-primary"
              >
                {label}
              </Link>
            )
          )}
        </div>
        {contactEmail ? (
          <a
            href={`mailto:${contactEmail}`}
            data-testid="footer-contact-email"
            className="mono mt-8 inline-block text-[10px] tracking-widest text-primary hover:underline"
          >
            {contactEmail.toUpperCase()}
          </a>
        ) : null}
        <p className="mono mt-8 text-[10px] leading-relaxed tracking-widest text-muted-foreground">
          WHEN LIFE GIVES YOU LEMONS, SMASH THEM INTO OPPORTUNITIES.
        </p>
      </div>
    </footer>
  );
};
